import {Item} from "./Item";
import {Draft} from "../Draft";

export class DraftItem extends Item {
  private base: Draft;

  constructor( draft: Draft ) {
    super();
    this.base = draft;
  }

  getBase(): any {
    return this.base;
  }

  getBindingType(): string {
    return this.base.bindingType;
  }

  // NOTE: do NOT override Item.getCost() here. The base class drives
  // cost off the fetched ProductionRateTable (via `computeCost()` and
  // the cached `this.cost`); overriding to return a hard-coded 0
  // silently masks the real price, which is the cause of the
  // "cart shows $0.00" bug encountered 2026-06-25.

  getCoverImage(): string {
    return "";
  }

  getHeight(): number {
    if ( this.base.draftBody ) {
      return this.base.draftBody.height / 72;
    }
    return 0;
  }

  getId(): number {
    return this.base.getId();
  }

  getInteriorType(): string {
    return this.base.interiorType;
  }

  getLaminateType(): string {
    // Guard: draftCover is linked asynchronously (see
    // DraftService.getLinkData); a Draft added to the cart before its
    // cover has resolved would throw a null-ref here and take down
    // the rate-fetch payload construction. Falling back to '' keeps
    // the payload sound (server treats unknown laminate as no-match
    // for laminate-conditioned rate factors — same as any other
    // Draft where the cover isn't authored yet).
    if (this.base.draftCover) { return this.base.draftCover.laminateType; }
    return '';
  }

  getPages(): number {
    // Guard: draftBody is linked asynchronously (see
    // DraftService.getLinkData); a Draft added to the cart before its
    // body has resolved would throw here — leaving the rate-fetch
    // stuck in LOADING (see OrderService.getProductionRatesForItem,
    // which sets status before building the payload). Returning 0
    // lets the payload go out; the rate engine will decline to price
    // a 0-page book which the cart surfaces as INFEASIBLE — a
    // recoverable state the user can retry once the body arrives.
    if (this.base.draftBody) { return this.base.draftBody.numPages; }
    return 0;
  }

  getPaperType(): number {
    if (this.base.draftBody) { return this.base.draftBody.paperType_ID; }
    return 0;
  }

  getTitle(): string {
    return this.base.title;
  }

  getWeight(): number {
    return this.base.weight;
  }

  getWidth(): number {
    if ( this.base.draftBody ) {
      return this.base.draftBody.width / 72;
    }
    return 0;
  }
  getThickness(): number {
    return this.base.thickness;
  }
  getCaseQuantity(): number {
    return 0;
  }
  getLastUpdate(): Date {
    return this.base.lastUpdate;
  }
  getAvailableInventory(): any[] {
    return [];
  }

  /**
   * Draft-specific completeness check. The `pages`, `width`, `height`,
   * `paperType` fields all come from `draft.draftBody`; `laminateType`
   * comes from `draft.draftCover`. Both are attached asynchronously by
   * `DraftService.getLinkData()` (which pulls the referenced rows
   * through `DraftBodyService` / `DraftCoverService`). Until both
   * arrive, the accessors defensively return 0/'' — sending that to
   * the rate engine yields an empty rate table which the cart caches
   * as `READY` (indistinguishable from a truly unpriceable item),
   * defeating the retry path. Gate the request on this check instead.
   *
   * `cover_ID === 0` is treated as "no cover on file yet" (a legitimate
   * Draft-in-progress state) and does NOT block the request — the
   * accessor returns '' which the server correctly treats as "no
   * laminate", matching an unlaminated rate row.
   */
  hasCompleteRateInputs(): boolean {
    if (!this.base) { return false; }
    if (this.base.body_ID && !this.base.draftBody) { return false; }
    if (this.base.cover_ID && !this.base.draftCover) { return false; }
    return true;
  }

}

